import type { PropsWithChildren } from 'react';
import { Component } from 'react';

import { logger } from '~/lib/logger';

class ErrorBoundary<
  T extends PropsWithChildren<{
    fallback?: PropsWithChildren['children'];
    onError?: (error: Error) => void;
  }>,
> extends Component<T, { error?: string }> {
  static displayName = 'ErrorBoundary';
  constructor(props: T) {
    super(props);
    this.state = { error: undefined };
  }

  componentDidCatch(error: Error) {
    logger.error(error);
    this.setState({ error: `${error.name}: ${error.message}` });
    this.props.onError?.(error);

    const currentURL = window.location.hostname;
    const isStaging = currentURL.includes('staging');
    const isDev = currentURL.includes('dev');
    const isLocal = currentURL.includes('localhost');

    const isNetworkError = error.message.includes(
      'Failed to fetch dynamically imported module',
    );

    if (isNetworkError && (isDev || isLocal || isStaging)) {
      let environment = '';

      if (isStaging) {
        environment = 'staging';
      } else if (isDev) {
        environment = 'development';
      } else if (isLocal) {
        environment = 'local';
      }

      const errorMessage = `It seems there is a connection issue in the ${environment} environment. Common issues may include: (1) VPN disconnection (2) misconfigured environment variables (3) Invalid x-coop-variant header or your PR stack hasn't deployed. Please verify and try again.`;

      this.setState({ error: errorMessage });
    } else if (isNetworkError) {
      const prodErrorMessage =
        'A connection issue has occurred. Please check your network settings and try again.';

      this.setState({ error: prodErrorMessage });
    }
  }

  render() {
    const { error } = this.state;

    if (error) {
      return this.props.fallback || <div>{error}</div>;
    }

    return <>{this.props.children}</>;
  }
}

export { ErrorBoundary };
